import { useEffect, useState, useRef, useCallback } from 'react';
import { api, errMsg } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { formatNumber, formatPct, formatSentiment, relativeTime, highlightBrand } from '@/lib/format';
import { PROVIDERS } from '@/constants/providers';
import { toast } from 'sonner';
import { Play, Loader2, ScanLine, RefreshCw, X, ExternalLink, Zap, Cpu } from 'lucide-react';
import { EmptyState } from '@/components/ChartCard';
import { ProviderChip } from '@/components/ProviderChip';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';

function statusVariant(status) {
  if (status === 'completed') return 'default';
  if (status === 'failed') return 'destructive';
  if (status === 'running') return 'secondary';
  return 'outline';
}

export default function ScansTab({ project, initialScanId, onSelectScan }) {
  const [scans, setScans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [openScan, setOpenScan] = useState(null);
  const [scanResults, setScanResults] = useState([]);
  const [resultsLoading, setResultsLoading] = useState(false);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/projects/${project.id}/scans`);
      setScans(r.data || []);
    } catch (_e) {
      // silent
    } finally {
      setLoading(false);
    }
  }, [project.id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const anyActive = scans.some((s) => s.status === 'running' || s.status === 'queued');
    if (anyActive && !pollRef.current) {
      pollRef.current = setInterval(load, 2500);
    }
    if (!anyActive && pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [scans, load]);

  // Open scan panel if initialScanId given
  useEffect(() => {
    if (initialScanId && scans.length && !openScan) {
      const s = scans.find((x) => x.id === initialScanId);
      if (s) openScanPanel(s);
    }
    // eslint-disable-next-line
  }, [initialScanId, scans]);

  const openScanPanel = async (s) => {
    setOpenScan(s);
    setResultsLoading(true);
    try {
      const r = await api.get(`/projects/${project.id}/scans/${s.id}/results`);
      setScanResults(r.data || []);
    } catch (_e) {
      toast.error('Failed to load scan results');
    } finally {
      setResultsLoading(false);
    }
    if (onSelectScan) onSelectScan(s.id);
  };

  // Auto-refresh results if the open scan is still running
  useEffect(() => {
    if (!openScan) return;
    if (openScan.status !== 'running' && openScan.status !== 'queued') return;
    const t = setInterval(async () => {
      try {
        const sr = await api.get(`/projects/${project.id}/scans/${openScan.id}`);
        setOpenScan(sr.data);
        const rr = await api.get(`/projects/${project.id}/scans/${openScan.id}/results`);
        setScanResults(rr.data || []);
        if (sr.data.status === 'completed' || sr.data.status === 'failed') {
          clearInterval(t);
          load();
        }
      } catch (_e) { /* ignore */ }
    }, 2500);
    return () => clearInterval(t);
  }, [openScan, project.id, load]);

  const runScan = async () => {
    setRunning(true);
    try {
      const r = await api.post(`/projects/${project.id}/scans`, {});
      toast.success('Scan started');
      await load();
      const fresh = await api.get(`/projects/${project.id}/scans/${r.data.id}`);
      openScanPanel(fresh.data);
    } catch (err) {
      // 402 (plan gate) already shows a rich Upgrade toast via the interceptor.
      if (err?.response?.status !== 402) {
        toast.error(errMsg(err, 'Failed to start scan'));
      }
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-display text-base font-semibold">Scans</h3>
          <p className="text-sm text-muted-foreground">Each scan runs every prompt across enabled providers.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={load} data-testid="scans-refresh-button"><RefreshCw className="h-4 w-4 mr-1.5" /> Refresh</Button>
          <Button size="sm" onClick={runScan} disabled={running} data-testid="scans-run-button">
            {running ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Play className="h-4 w-4 mr-1.5" />} Run scan
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="space-y-2">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-16" />)}</div>
      ) : scans.length === 0 ? (
        <Card className="border p-8">
          <EmptyState
            title="No scans yet"
            description="Run your first scan to see how AI answers talk about your brand."
            action={<Button onClick={runScan} disabled={running} data-testid="scans-empty-run-button"><Play className="h-4 w-4 mr-1.5" /> Run first scan</Button>}
          />
        </Card>
      ) : (
        <div className="space-y-2">
          {scans.map((s) => {
            const pct = s.total_units ? (s.completed_units / s.total_units) : 0;
            return (
              <Card key={s.id} data-testid={`table-scans-row-${s.id}`} onClick={() => openScanPanel(s)} className="border p-4 cursor-pointer hover:bg-secondary/40 transition-colors">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <ScanLine className="h-4 w-4 text-muted-foreground" />
                      <div className="font-medium">Scan — {relativeTime(s.created_at)}</div>
                      <Badge variant={statusVariant(s.status)} className="capitalize">{s.status}</Badge>
                    </div>
                    <div className="mt-1 text-xs text-muted-foreground">
                      {formatNumber(s.completed_units)} / {formatNumber(s.total_units)} units · {s.providers.length} providers · {s.prompt_ids.length} prompts
                    </div>
                    {(s.status === 'running' || s.status === 'queued') && (
                      <Progress value={Math.round(pct * 100)} className="mt-2 h-1.5" />
                    )}
                  </div>
                  {s.summary && (
                    <div className="grid grid-cols-3 gap-4 text-right min-w-[260px]">
                      <div>
                        <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Mentions</div>
                        <div className="font-display font-semibold tabular">{formatPct(s.summary.mention_rate)}</div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Sentiment</div>
                        <div className="font-display font-semibold tabular">{s.summary.avg_sentiment?.toFixed(2)}</div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Citations</div>
                        <div className="font-display font-semibold tabular">{formatNumber(Object.keys(s.summary.citation_domains || {}).length)}</div>
                      </div>
                    </div>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <Sheet open={!!openScan} onOpenChange={(v) => { if (!v) setOpenScan(null); }}>
        <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto">
          {openScan && (
            <ScanDrilldown project={project} scan={openScan} results={scanResults} loading={resultsLoading} onClose={() => setOpenScan(null)} />
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function ScanDrilldown({ project, scan, results, loading, onClose }) {
  const brand = project.name;
  const grouped = results.reduce((acc, r) => {
    (acc[r.prompt_id] = acc[r.prompt_id] || []).push(r);
    return acc;
  }, {});
  const promptIds = Object.keys(grouped);
  const [expandedResult, setExpandedResult] = useState(null);

  return (
    <div>
      <SheetHeader className="mb-4">
        <div className="flex items-start justify-between">
          <SheetTitle className="font-display">Scan drill-down</SheetTitle>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button>
        </div>
        <div className="text-sm text-muted-foreground">
          {relativeTime(scan.created_at)} · <span className="capitalize">{scan.status}</span> · {scan.completed_units}/{scan.total_units} units
        </div>
        <div className="flex flex-wrap gap-2 mt-2">
          {scan.providers.map((p) => <ProviderChip key={p} providerKey={p} active={scan.status === 'running'} />)}
        </div>
        {scan.summary && (
          <div className="grid grid-cols-4 gap-2 mt-3">
            <MiniStat label="Mentions" value={formatPct(scan.summary.mention_rate)} />
            <MiniStat label="Sentiment" value={scan.summary.avg_sentiment?.toFixed(2)} />
            <MiniStat label="Answers" value={formatNumber(scan.summary.successful_results)} />
            <MiniStat label="Failed" value={formatNumber(scan.failed_units)} />
          </div>
        )}
      </SheetHeader>

      {loading ? (
        <div className="space-y-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-24" />)}</div>
      ) : promptIds.length === 0 ? (
        <div className="text-sm text-muted-foreground">No results yet.</div>
      ) : (
        <div className="space-y-4">
          {promptIds.map((pid) => {
            const items = grouped[pid];
            const promptText = items[0]?.prompt_text || '';
            return (
              <Card key={pid} className="border p-4">
                <div className="text-sm font-medium">{promptText}</div>
                <div className="mt-3 space-y-2">
                  {items.map((r) => {
                    const meta = PROVIDERS[r.provider] || { label: r.provider };
                    const analysis = r.analysis || {};
                    const mentioned = !!analysis.brand_mentioned;
                    const expanded = expandedResult === r.id;
                    return (
                      <div key={r.id} className="rounded-lg border" data-testid={`scan-result-${r.id}`}>
                        <button className="w-full text-left p-3 flex items-start justify-between gap-3" onClick={() => setExpandedResult(expanded ? null : r.id)}>
                          <div className="flex items-center gap-2 flex-wrap">
                            <ProviderChip providerKey={r.provider} />
                            {r.status === 'success' ? (
                              <Badge variant={mentioned ? 'default' : 'outline'} className="text-[11px]">
                                {mentioned ? 'Mentioned' : 'Not mentioned'}
                              </Badge>
                            ) : r.status === 'failed' ? (
                              <Badge variant="destructive" className="text-[11px]">Failed</Badge>
                            ) : (
                              <Badge variant="secondary" className="text-[11px]">Pending</Badge>
                            )}
                            {r.status === 'success' && analysis.provider_mode === 'native' && (
                              <Badge
                                variant="outline"
                                className="text-[11px] border-emerald-500/40 text-emerald-700 bg-emerald-500/10 gap-1"
                                title="Response was generated live via the provider's native web_search / grounding tool. Citations are real, real-time URLs."
                                data-testid={`scan-result-mode-${r.id}`}
                              >
                                <Zap className="h-3 w-3" /> Live web
                              </Badge>
                            )}
                            {r.status === 'success' && analysis.provider_mode && analysis.provider_mode !== 'native' && (
                              <Badge
                                variant="outline"
                                className="text-[11px] text-muted-foreground gap-1"
                                title="Base-model response (no live web search). Add the provider's API key to enable real-time grounded citations."
                                data-testid={`scan-result-mode-${r.id}`}
                              >
                                <Cpu className="h-3 w-3" /> Base model
                              </Badge>
                            )}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {r.status === 'success' && analysis.sentiment ? `${formatSentiment(analysis.sentiment.score)}` : ''}
                          </div>
                        </button>
                        {expanded && (
                          <div className="border-t p-3 space-y-3">
                            {r.status === 'success' ? (
                              <>
                                <div>
                                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">AI answer ({meta.label})</div>
                                  <div className="text-sm leading-relaxed whitespace-pre-wrap"
                                    dangerouslySetInnerHTML={{ __html: highlightBrand(r.response_text, brand) }} />
                                </div>
                                {analysis.summary && (
                                  <div className="text-xs text-muted-foreground italic">“{analysis.summary}”</div>
                                )}
                                {analysis.citations_breakdown && analysis.citations_breakdown.total > 0 && (
                                  <div className="flex flex-wrap gap-1.5" data-testid={`scan-citations-breakdown-${r.id}`}>
                                    <Badge variant="outline" className="text-[10px] border-emerald-500/40 text-emerald-700 bg-emerald-500/10">
                                      Brand · {analysis.citations_breakdown.brand}
                                    </Badge>
                                    <Badge variant="outline" className="text-[10px] border-amber-500/40 text-amber-700 bg-amber-500/10">
                                      Competitors · {analysis.citations_breakdown.competitor}
                                    </Badge>
                                    <Badge variant="outline" className="text-[10px] text-muted-foreground">
                                      Third-party · {analysis.citations_breakdown.third_party}
                                    </Badge>
                                  </div>
                                )}
                                {analysis.citations && analysis.citations.length > 0 && (
                                  <div>
                                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">
                                      Citations ({analysis.citations.length})
                                    </div>
                                    <ul className="space-y-1 text-xs">
                                      {analysis.citations.slice(0, 10).map((c, i) => {
                                        const owner = c.owner || 'third_party';
                                        const ownerCls =
                                          owner === 'brand'
                                            ? 'border-emerald-500/40 text-emerald-700 bg-emerald-500/10'
                                            : owner === 'third_party'
                                              ? 'text-muted-foreground'
                                              : 'border-amber-500/40 text-amber-700 bg-amber-500/10';
                                        const ownerLabel = owner === 'brand' ? 'Brand' : owner === 'third_party' ? '3rd party' : owner;
                                        return (
                                          <li key={i} className="flex items-center gap-2">
                                            <Badge variant="outline" className={`text-[10px] shrink-0 ${ownerCls}`}>{ownerLabel}</Badge>
                                            <a href={c.url} target="_blank" rel="noreferrer" className="text-[hsl(var(--accent))] hover:underline inline-flex items-center gap-1 min-w-0 truncate" title={c.title || c.url}>
                                              <ExternalLink className="h-3 w-3 shrink-0" />
                                              <span className="truncate">{c.domain || c.url}</span>
                                            </a>
                                          </li>
                                        );
                                      })}
                                    </ul>
                                  </div>
                                )}
                                {analysis.competitor_mentions && analysis.competitor_mentions.length > 0 && (
                                  <div>
                                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Competitors mentioned</div>
                                    <div className="flex flex-wrap gap-1.5">
                                      {analysis.competitor_mentions.map((cm) => (
                                        <Badge key={cm.name} variant="secondary" className="text-[11px]">{cm.name} · {cm.count}</Badge>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                {analysis.native_error && (
                                  <details className="text-[11px] text-muted-foreground">
                                    <summary className="cursor-pointer hover:text-foreground">Why base model? (native call error)</summary>
                                    <div className="mt-1 font-mono text-[10px] bg-secondary/50 rounded p-2 break-all">{analysis.native_error}</div>
                                  </details>
                                )}
                              </>
                            ) : r.status === 'failed' ? (
                              <div className="text-xs text-rose-600">Error: {r.error || 'Unknown failure'}</div>
                            ) : (
                              <div className="text-xs text-muted-foreground">Pending…</div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className="rounded-md border p-2">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="font-display font-semibold tabular text-sm">{value ?? '—'}</div>
    </div>
  );
}
